import React, { useState, useEffect, useMemo, useContext } from 'react';
import {
  Typography,
  Chip,
} from '@material-tailwind/react';
import {
  AdjustmentsHorizontalIcon,
} from '@heroicons/react/24/outline';
import Select from 'react-select';
import { DataContext } from '../../context/DataContext';

function SubjectAircraftPanel({ draft, onValuesChange, onDataChanged, resetKey }) {
  // Get manufacturer/model data from context
  const dataContext = useContext(DataContext);
  const manufacturerData = dataContext.getManufacturerData();
  const modelData = dataContext.getModelData();

  // Original values from draft (for change detection)
  const originalSubjectAircraft = useMemo(() => draft?.subjectAircraft || {}, [draft?.subjectAircraft]);

  // Form state
  const [registration, setRegistration] = useState(originalSubjectAircraft.registration || '');
  const [selectedManufacturer, setSelectedManufacturer] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [year, setYear] = useState(originalSubjectAircraft.year?.toString() || '');
  const [hours, setHours] = useState(originalSubjectAircraft.totalTime?.toString() || '');
  const [additionalDetails, setAdditionalDetails] = useState(originalSubjectAircraft.additionalDetails || '');

  // Build manufacturer options from DataContext
  const manufacturerOptions = useMemo(() => {
    if (!Object.keys(manufacturerData).length) return [];
    return Object.keys(manufacturerData)
      .sort()
      .map((k) => ({ value: manufacturerData[k], label: k }));
  }, [manufacturerData]);

  // Build model options filtered by selected manufacturer
  const modelOptions = useMemo(() => {
    if (!Object.keys(modelData).length || !selectedManufacturer) return [];

    const options = [];
    for (const [key, value] of Object.entries(modelData)) {
      const manufacturersForModel = new Set(value.manufacturers);
      const manufacturerIds = new Set(selectedManufacturer.value?.ids || []);
      const hasIntersection = manufacturersForModel.intersection
        ? manufacturersForModel.intersection(manufacturerIds).size > 0
        : [...manufacturersForModel].some(id => manufacturerIds.has(id));

      if (hasIntersection) {
        options.push({ value: value, label: key });
      }
    }
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [modelData, selectedManufacturer]);

  // Reset form when switching to a different draft or when resetKey changes (discard)
  useEffect(() => {
    const sa = draft?.subjectAircraft || {};
    setRegistration(sa.registration || '');
    setYear(sa.year?.toString() || '');
    setHours(sa.totalTime?.toString() || '');
    setAdditionalDetails(sa.additionalDetails || '');

    // Find matching manufacturer option
    if (sa.manufacturer && Object.keys(manufacturerData).length) {
      const matchingMfr = Object.keys(manufacturerData).find(
        k => k.toLowerCase() === sa.manufacturer.toLowerCase()
      );
      if (matchingMfr) {
        setSelectedManufacturer({ value: manufacturerData[matchingMfr], label: matchingMfr });
      } else {
        // Allow custom value if not in list
        setSelectedManufacturer({ value: { ids: [] }, label: sa.manufacturer });
      }
    } else {
      setSelectedManufacturer(null);
    }

    // Find matching model option
    if (sa.model && Object.keys(modelData).length) {
      const matchingModel = Object.keys(modelData).find(
        k => k.toLowerCase() === sa.model.toLowerCase()
      );
      if (matchingModel) {
        setSelectedModel({ value: modelData[matchingModel], label: matchingModel });
      } else {
        // Allow custom value if not in list
        setSelectedModel({ value: {}, label: sa.model });
      }
    } else {
      setSelectedModel(null);
    }
  }, [draft?.draftId, draft?.subjectAircraft, manufacturerData, modelData, resetKey]);

  // Detect what changed
  const dataChanged = useMemo(() => {
    const orig = originalSubjectAircraft;
    const currentManufacturer = selectedManufacturer?.label || '';
    const currentModel = selectedModel?.label || '';
    return (
      (registration || '') !== (orig.registration || '') ||
      currentManufacturer !== (orig.manufacturer || '') ||
      currentModel !== (orig.model || '') ||
      (year || '') !== (orig.year?.toString() || '') ||
      (hours || '') !== (orig.totalTime?.toString() || '') ||
      (additionalDetails || '') !== (orig.additionalDetails || '')
    );
  }, [registration, selectedManufacturer, selectedModel, year, hours, additionalDetails, originalSubjectAircraft]);

  // Notify parent of current values whenever they change
  useEffect(() => {
    if (onValuesChange) {
      onValuesChange({
        registration,
        manufacturer: selectedManufacturer?.label || '',
        model: selectedModel?.label || '',
        year: year ? parseInt(year, 10) : undefined,
        hours: hours ? parseInt(hours, 10) : undefined,
        additionalDetails,
      });
    }
  }, [registration, selectedManufacturer, selectedModel, year, hours, additionalDetails, onValuesChange]);

  // Handle manufacturer change - clear model when manufacturer changes
  const handleManufacturerChange = (option) => {
    setSelectedManufacturer(option);
    setSelectedModel(null);
  };

  // Notify parent of change status
  useEffect(() => {
    if (onDataChanged) {
      onDataChanged(dataChanged);
    }
  }, [dataChanged, onDataChanged]);

  return (
 <>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <AdjustmentsHorizontalIcon className="h-5 w-5 text-blue-500" />
            <Typography variant="h6" color="blue-gray">
              Subject Aircraft
            </Typography>
          </div>
          {dataChanged && (
            <Chip
              size="sm"
              value="Modified"
              color="amber"
              variant="ghost"
            />
          )}
        </div>

        <div className="flex flex-col gap-4">
          <div>
            <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
              Registration *
            </Typography>
            <input
              type="text"
              value={registration}
              onChange={(e) => setRegistration(e.target.value.toUpperCase())}
              placeholder="e.g., N123AB"
              className="w-full px-3 py-2 border border-blue-gray-200 rounded-md focus:outline-none focus:border-blue-500"
            />
          </div>

          <div>
            <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
              Manufacturer
            </Typography>
            <Select
              options={manufacturerOptions}
              value={selectedManufacturer}
              onChange={handleManufacturerChange}
              placeholder="Select manufacturer..."
              isClearable
              menuPortalTarget={document.body}
              styles={{
                control: (base) => ({ ...base, minHeight: '40px' }),
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />
          </div>

          <div>
            <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
              Model
            </Typography>
            <Select
              options={modelOptions}
              value={selectedModel}
              onChange={setSelectedModel}
              placeholder={selectedManufacturer ? "Select model..." : "Select manufacturer first"}
              isDisabled={!selectedManufacturer}
              isClearable
              menuPortalTarget={document.body}
              styles={{
                control: (base) => ({ ...base, minHeight: '40px' }),
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />
          </div>

          <div>
            <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
              Model Year
            </Typography>
            <input
              type="number"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              placeholder="e.g., 2019"
              className="w-full px-3 py-2 border border-blue-gray-200 rounded-md focus:outline-none focus:border-blue-500"
            />
          </div>

          <div>
            <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
              Total Hours
            </Typography>
            <input
              type="number"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              placeholder="e.g., 850"
              className="w-full px-3 py-2 border border-blue-gray-200 rounded-md focus:outline-none focus:border-blue-500"
            />
          </div>

          <div>
            <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
              Additional Details
            </Typography>
            <textarea
              value={additionalDetails}
              onChange={(e) => setAdditionalDetails(e.target.value)}
              placeholder="Equipment, upgrades, condition..."
              rows={2}
              className="w-full px-3 py-2 border border-blue-gray-200 rounded-md focus:outline-none focus:border-blue-500 resize-none"
            />
          </div>
        </div>
        </>

  );
}

export default SubjectAircraftPanel;
